import { useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { usePermissions } from "@/hooks/usePermissions";
import MemberNameCell from "@/components/MemberNameCell";
import MemberDetailDialog from "@/components/MemberDetailDialog";
import { ArrowDown, ArrowLeft, ArrowUp, ArrowUpDown, CalendarDays, CheckCircle2, Church, Clock, Loader2, MapPin, Pencil, Plus, Trash2, UserPlus, Users } from "lucide-react";
import { format, parseISO } from "date-fns";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import type { Tables } from "@/integrations/supabase/types";

const MINISTRY_NOTE_PREFIX = "Ministry: ";
const MINISTRY_IDS_NOTE_PREFIX = "Ministry IDs: ";
const LEGACY_ORG_NOTE_PREFIX = "Organization: ";
const LEGACY_ORG_IDS_NOTE_PREFIX = "Organization IDs: ";

type OrganizationNode = {
  id: string;
  name: string;
  parent_id: string | null;
};

type EventDetail = {
  id: string;
  name: string;
  event_type: string;
  event_date: string;
  start_time: string | null;
  end_time: string | null;
  location: string | null;
  notes: string | null;
  is_active: boolean;
  flyer_url: string | null;
};

type AttendanceRecord = {
  id: string;
  event_id: string;
  member_name: string;
  member_id: string | null;
  phone: string | null;
  email: string | null;
  is_visitor: boolean;
  status: string;
  checkin_time: string;
};

type Member = Tables<"members">;

type MemberSearchResult = {
  id: string;
  full_name: string;
  phone: string | null;
};

type AttendeeFormValues = {
  member_name: string;
  member_id: string | null;
  phone: string;
  email: string;
  is_visitor: boolean;
  status: string;
};

type SortDir = "asc" | "desc" | null;
type AttendeeSortField = "member_name" | "is_visitor" | "status" | "checkin_time" | "";

const EMPTY_FORM: AttendeeFormValues = {
  member_name: "",
  member_id: null,
  phone: "",
  email: "",
  is_visitor: false,
  status: "present",
};

const getOrganizationFromNotes = (notes: string | null) => {
  if (!notes) return "";
  const firstLine = notes.split("\n")[0]?.trim() || "";
  if (firstLine.startsWith(MINISTRY_NOTE_PREFIX)) return firstLine.replace(MINISTRY_NOTE_PREFIX, "").trim();
  if (firstLine.startsWith(LEGACY_ORG_NOTE_PREFIX)) return firstLine.replace(LEGACY_ORG_NOTE_PREFIX, "").trim();
  return "";
};

const getOrganizationIdsFromNotes = (notes: string | null) => {
  if (!notes) return [] as string[];
  const lines = notes.split("\n").map((line) => line.trim());
  const idsLine = lines.find((line) =>
    line.startsWith(MINISTRY_IDS_NOTE_PREFIX) || line.startsWith(LEGACY_ORG_IDS_NOTE_PREFIX)
  );
  if (!idsLine) return [] as string[];
  return idsLine
    .replace(MINISTRY_IDS_NOTE_PREFIX, "")
    .replace(LEGACY_ORG_IDS_NOTE_PREFIX, "")
    .split(">")
    .map((id) => id.trim())
    .filter(Boolean);
};

export default function EventAttendancePage() {
  const { eventId } = useParams<{ eventId: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { role } = useAuth();
  const { getAttendanceOrganizationIds, hasAttendanceScopedAccess } = usePermissions();
  const allowedAttendanceOrgIds = getAttendanceOrganizationIds();
  const attendanceScoped = role !== "admin" && hasAttendanceScopedAccess();

  const [formOpen, setFormOpen] = useState(false);
  const [editingAttendee, setEditingAttendee] = useState<AttendanceRecord | null>(null);
  const [deleteAttendee, setDeleteAttendee] = useState<AttendanceRecord | null>(null);
  const [viewMember, setViewMember] = useState<Member | null>(null);
  const [formValues, setFormValues] = useState<AttendeeFormValues>(EMPTY_FORM);
  const [sortField, setSortField] = useState<AttendeeSortField>("checkin_time");
  const [sortDir, setSortDir] = useState<SortDir>("desc");
  const [attendeeSearch, setAttendeeSearch] = useState("");
  const [typeFilter, setTypeFilter] = useState<"all" | "member" | "visitor">("all");
  const [statusFilter, setStatusFilter] = useState<"all" | "present" | "late" | "absent">("all");

  const { data: event, isLoading: eventLoading } = useQuery({
    queryKey: ["attendance_event_detail", eventId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("events")
        .select("id, name, event_type, event_date, start_time, end_time, location, notes, is_active, flyer_url")
        .eq("id", eventId!)
        .eq("is_active", true)
        .maybeSingle();
      if (error) throw error;
      return (data || null) as EventDetail | null;
    },
    enabled: !!eventId,
  });

  const { data: organizations = [] } = useQuery({
    queryKey: ["organizations", "attendance_scope"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("organizations")
        .select("id, name, parent_id");
      if (error) throw error;
      return (data || []) as OrganizationNode[];
    },
  });

  const scopedOrgSet = useMemo(() => {
    if (!attendanceScoped) return null;

    const byParent = new Map<string, string[]>();
    organizations.forEach((org) => {
      if (!org.parent_id) return;
      const current = byParent.get(org.parent_id) || [];
      current.push(org.id);
      byParent.set(org.parent_id, current);
    });

    const visited = new Set<string>();
    const queue = [...allowedAttendanceOrgIds];
    while (queue.length > 0) {
      const currentId = queue.shift()!;
      if (visited.has(currentId)) continue;
      visited.add(currentId);
      const children = byParent.get(currentId) || [];
      children.forEach((childId) => {
        if (!visited.has(childId)) queue.push(childId);
      });
    }

    return visited;
  }, [attendanceScoped, organizations, allowedAttendanceOrgIds]);

  const organizationNameToIds = useMemo(() => {
    const map = new Map<string, string[]>();
    organizations.forEach((org) => {
      const key = org.name.toLowerCase();
      const existing = map.get(key) || [];
      existing.push(org.id);
      map.set(key, existing);
    });
    return map;
  }, [organizations]);

  const hasScopedAccessToEvent = useMemo(() => {
    if (!attendanceScoped) return true;
    if (!event || !scopedOrgSet) return false;

    const orgIdsFromNotes = getOrganizationIdsFromNotes(event.notes);
    if (orgIdsFromNotes.length > 0) {
      return orgIdsFromNotes.some((orgId) => scopedOrgSet.has(orgId));
    }

    const orgPath = getOrganizationFromNotes(event.notes);
    if (!orgPath) return false;
    const orgNames = orgPath.split(">").map((part) => part.trim().toLowerCase()).filter(Boolean);
    if (orgNames.length === 0) return false;

    return orgNames.some((name) => {
      const matchedIds = organizationNameToIds.get(name) || [];
      return matchedIds.some((id) => scopedOrgSet.has(id));
    });
  }, [attendanceScoped, event, scopedOrgSet, organizationNameToIds]);

  const { data: attendees = [], isLoading: attendeesLoading } = useQuery({
    queryKey: ["event_attendees", eventId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("attendance_records")
        .select("id, event_id, member_name, member_id, phone, email, is_visitor, status, checkin_time")
        .eq("event_id", eventId!)
        .order("checkin_time", { ascending: false });
      if (error) throw error;
      return (data || []) as AttendanceRecord[];
    },
    enabled: !!eventId && !!event && hasScopedAccessToEvent,
  });

  const attendeeMemberIds = useMemo(
    () => [...new Set(attendees.map((a) => a.member_id).filter(Boolean) as string[])],
    [attendees]
  );

  const { data: attendeeMembers = [] } = useQuery({
    queryKey: ["event_attendance_members", attendeeMemberIds],
    queryFn: async () => {
      if (attendeeMemberIds.length === 0) return [] as Member[];
      const { data, error } = await supabase
        .from("members")
        .select("*")
        .in("id", attendeeMemberIds);
      if (error) throw error;
      return (data || []) as Member[];
    },
    enabled: attendeeMemberIds.length > 0,
  });

  const attendeeMemberById = useMemo(
    () => new Map(attendeeMembers.map((member) => [member.id, member])),
    [attendeeMembers]
  );

  const { data: memberSuggestions = [] } = useQuery({
    queryKey: ["attendance_member_search", eventId, formValues.member_name, formValues.is_visitor, formOpen, editingAttendee?.id || "new"],
    queryFn: async () => {
      const search = formValues.member_name.trim();
      if (!eventId || formValues.is_visitor || search.length < 2) {
        return [] as MemberSearchResult[];
      }

      const { data, error } = await supabase.rpc("search_members_by_event" as any, {
        _event_id: eventId,
        _search: search,
      });
      if (error) throw error;

      return ((data || []) as any[]).map((member) => ({
        id: member.id,
        full_name: member.full_name,
        phone: member.phone || null,
      })) as MemberSearchResult[];
    },
    enabled: formOpen && !editingAttendee && !!eventId && !formValues.is_visitor && !formValues.member_id && formValues.member_name.trim().length >= 2,
  });

  const filteredAttendees = useMemo(() => {
    const query = attendeeSearch.trim().toLowerCase();

    return attendees.filter((attendee) => {
      const member = attendee.member_id ? attendeeMemberById.get(attendee.member_id) : null;
      const displayPhone = attendee.is_visitor ? attendee.phone || "" : member?.phone || "";
      const displayEmail = attendee.is_visitor ? attendee.email || "" : member?.email || "";
      const displayStatus = (attendee.status || "present").toLowerCase();
      const displayType = attendee.is_visitor ? "visitor" : "member";

      const matchesSearch = !query || [
        attendee.member_name,
        displayPhone,
        displayEmail,
        displayType,
        displayStatus,
      ]
        .join(" ")
        .toLowerCase()
        .includes(query);

      const matchesType = typeFilter === "all" || displayType === typeFilter;
      const matchesStatus = statusFilter === "all" || displayStatus === statusFilter;

      return matchesSearch && matchesType && matchesStatus;
    });
  }, [attendees, attendeeMemberById, attendeeSearch, typeFilter, statusFilter]);

  const sortedAttendees = useMemo(() => {
    if (!sortField || !sortDir) return filteredAttendees;

    const sorted = [...filteredAttendees].sort((a, b) => {
      if (sortField === "member_name") {
        return a.member_name.localeCompare(b.member_name, undefined, { sensitivity: "base" });
      }

      if (sortField === "is_visitor") {
        const aValue = a.is_visitor ? "Visitor" : "Member";
        const bValue = b.is_visitor ? "Visitor" : "Member";
        return aValue.localeCompare(bValue, undefined, { sensitivity: "base" });
      }

      if (sortField === "status") {
        return (a.status || "").localeCompare(b.status || "", undefined, { sensitivity: "base" });
      }

      // checkin_time
      return new Date(a.checkin_time).getTime() - new Date(b.checkin_time).getTime();
    });

    return sortDir === "asc" ? sorted : sorted.reverse();
  }, [filteredAttendees, sortField, sortDir]);

  const toggleSort = (field: AttendeeSortField) => {
    if (sortField === field) {
      if (sortDir === "asc") {
        setSortDir("desc");
      } else if (sortDir === "desc") {
        setSortField("");
        setSortDir(null);
      }
      return;
    }

    setSortField(field);
    setSortDir("asc");
  };

  const SortIcon = ({ field }: { field: Exclude<AttendeeSortField, ""> }) => {
    const active = sortField === field && !!sortDir;
    if (!active) {
      return <ArrowUpDown className="w-3 h-3 ml-1 opacity-0 transition-opacity group-hover:opacity-70" />;
    }
    return sortDir === "asc"
      ? <ArrowUp className="w-3 h-3 ml-1 opacity-100" />
      : <ArrowDown className="w-3 h-3 ml-1 opacity-100" />;
  };

  const refreshAttendance = () => {
    queryClient.invalidateQueries({ queryKey: ["event_attendees", eventId] });
    queryClient.invalidateQueries({ queryKey: ["attendance"] });
    queryClient.invalidateQueries({ queryKey: ["event_counts"] });
  };

  const addAttendeeMutation = useMutation({
    mutationFn: async (values: AttendeeFormValues) => {
      const payload = {
        event_id: eventId!,
        member_name: values.member_name.trim(),
        member_id: values.member_id,
        phone: values.is_visitor ? values.phone.trim() || null : null,
        email: values.is_visitor ? values.email.trim() || null : null,
        is_visitor: values.is_visitor,
        status: values.status,
      };
      const { error } = await supabase.from("attendance_records").insert(payload);
      if (error) throw error;
    },
    onSuccess: () => {
      refreshAttendance();
      
      setFormOpen(false);
      setFormValues(EMPTY_FORM);
    },
    onError: (err: Error) => toast.error(err.message),
  });

  const updateAttendeeMutation = useMutation({
    mutationFn: async ({ id, values }: { id: string; values: AttendeeFormValues }) => {
      const payload = {
        member_name: values.member_name.trim(),
        member_id: values.member_id,
        phone: values.is_visitor ? values.phone.trim() || null : null,
        email: values.is_visitor ? values.email.trim() || null : null,
        is_visitor: values.is_visitor,
        status: values.status,
      };
      const { error } = await supabase
        .from("attendance_records")
        .update(payload)
        .eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => {
      refreshAttendance();
      
      setFormOpen(false);
      setEditingAttendee(null);
      setFormValues(EMPTY_FORM);
    },
    onError: (err: Error) => toast.error(err.message),
  });

  const deleteAttendeeMutation = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from("attendance_records")
        .delete()
        .eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => {
      refreshAttendance();
      
      setDeleteAttendee(null);
    },
    onError: (err: Error) => toast.error(err.message),
  });

  const openAddDialog = () => {
    setEditingAttendee(null);
    setFormValues(EMPTY_FORM);
    setFormOpen(true);
  };

  const openEditDialog = (attendee: AttendanceRecord) => {
    setEditingAttendee(attendee);
    setFormValues({
      member_name: attendee.member_name,
      member_id: attendee.member_id,
      phone: attendee.phone || "",
      email: attendee.email || "",
      is_visitor: attendee.is_visitor,
      status: attendee.status || "present",
    });
    setFormOpen(true);
  };

  const applyMemberSuggestion = (member: MemberSearchResult) => {
    setFormValues((prev) => ({
      ...prev,
      member_name: member.full_name,
      member_id: member.id,
      phone: prev.phone || member.phone || "",
      email: "",
      is_visitor: false,
    }));
  };

  const saveAttendee = () => {
    if (!formValues.member_name.trim()) {
      toast.error("Attendee name is required");
      return;
    }

    if (editingAttendee) {
      updateAttendeeMutation.mutate({ id: editingAttendee.id, values: formValues });
    } else {
      addAttendeeMutation.mutate(formValues);
    }
  };

  const totalCount = attendees.length;
  const visitorCount = attendees.filter((a) => a.is_visitor).length;
  const memberCount = totalCount - visitorCount;

  if (!eventId) {
    return (
      <div className="space-y-4">
        <p className="text-muted-foreground">No event selected.</p>
        <Button variant="outline" onClick={() => navigate("/events")}>Back to Events</Button>
      </div>
    );
  }

  if (eventLoading) {
    return (
      <div className="min-h-[360px] flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!event) {
    return (
      <div className="space-y-4">
        <p className="text-muted-foreground">Event not found or no longer active.</p>
        <Button variant="outline" onClick={() => navigate("/events")}>Back to Events</Button>
      </div>
    );
  }

  if (!hasScopedAccessToEvent) {
    return (
      <div className="space-y-4">
        <p className="text-muted-foreground">You do not have permission to view attendance for this event.</p>
        <Button variant="outline" onClick={() => navigate("/events")}>Back to Events</Button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3">
        <Button variant="outline" onClick={() => navigate(`/events?eventId=${event.id}`)}>
          <ArrowLeft className="w-4 h-4 mr-2" /> Back
        </Button>
      </div>

      <div className="stat-card">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex items-start gap-4 min-w-0 flex-1">
            <div className="w-20 h-20 self-center shrink-0 rounded-lg border border-border/70 bg-secondary/30 overflow-hidden flex items-center justify-center">
              {event.flyer_url ? (
                <img
                  src={event.flyer_url}
                  alt={`${event.name} flyer`}
                  className="w-full h-full object-cover"
                />
              ) : (
                <Church className="w-8 h-8 text-muted-foreground/60" />
              )}
            </div>

            <div className="min-w-0 flex-1">
              <h2 className="text-xl font-heading font-semibold text-foreground">{event.name}</h2>
              <div className="mt-2 flex flex-wrap items-center gap-4 text-sm text-muted-foreground">
                <span className="inline-flex items-center gap-1.5">
                  <CalendarDays className="w-4 h-4" />
                  {format(parseISO(event.event_date), "MMMM d, yyyy")}
                </span>
                {event.start_time && (
                  <span className="inline-flex items-center gap-1.5">
                    <Clock className="w-4 h-4" />
                    {event.start_time.slice(0, 5)}{event.end_time ? ` - ${event.end_time.slice(0, 5)}` : ""}
                  </span>
                )}
                {event.location && (
                  <span className="inline-flex items-center gap-1.5">
                    <MapPin className="w-4 h-4" />
                    {event.location}
                  </span>
                )}
              </div>
              <div className="mt-4 flex flex-wrap gap-2">
                <Badge variant="secondary">{event.event_type}</Badge>
                <Badge variant="secondary">{memberCount} members</Badge>
                <Badge variant="secondary">{visitorCount} visitors</Badge>
                <Badge variant="secondary">{totalCount} total</Badge>
              </div>
            </div>
          </div>

          <Button className="sm:ml-4 sm:shrink-0 self-start sm:self-center" onClick={openAddDialog}>
            <Plus className="w-4 h-4 mr-2" /> Check In
          </Button>
        </div>
      </div>

      <div className="stat-card">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-heading font-semibold text-foreground inline-flex items-center gap-2">
            <Users className="w-4 h-4 text-primary" /> Attendees
          </h3>
        </div>

        {attendeesLoading ? (
          <div className="py-12 flex items-center justify-center">
            <Loader2 className="w-6 h-6 animate-spin text-primary" />
          </div>
        ) : attendees.length === 0 ? (
          <div className="text-center py-12">
            <p className="text-sm text-muted-foreground">No attendees yet for this event.</p>
            <Button className="mt-3" onClick={openAddDialog}>
              <Plus className="w-4 h-4 mr-2" /> Check In First Attendee
            </Button>
          </div>
        ) : (
          <div className="space-y-3">
            <div className="flex flex-col gap-2 md:flex-row md:items-center">
              <div className="flex-1">
                <Input
                  value={attendeeSearch}
                  onChange={(e) => setAttendeeSearch(e.target.value)}
                  placeholder="Search by name, email, phone, type, or status..."
                />
              </div>
              <div className="grid grid-cols-2 gap-2 md:flex md:items-center">
                <Select value={typeFilter} onValueChange={(value: "all" | "member" | "visitor") => setTypeFilter(value)}>
                  <SelectTrigger className="w-full md:w-[140px]"><SelectValue placeholder="Type" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All types</SelectItem>
                    <SelectItem value="member">Member</SelectItem>
                    <SelectItem value="visitor">Visitor</SelectItem>
                  </SelectContent>
                </Select>
                <Select value={statusFilter} onValueChange={(value: "all" | "present" | "late" | "absent") => setStatusFilter(value)}>
                  <SelectTrigger className="w-full md:w-[150px]"><SelectValue placeholder="Status" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All statuses</SelectItem>
                    <SelectItem value="present">Present</SelectItem>
                    <SelectItem value="late">Late</SelectItem>
                    <SelectItem value="absent">Absent</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="overflow-x-auto">
            <table className="w-full min-w-[980px]">
              <thead>
                <tr className="border-b border-border bg-secondary/30">
                  <th
                    className="group text-left text-xs font-medium text-muted-foreground uppercase tracking-wider px-4 py-3 cursor-pointer select-none hover:text-foreground transition-colors"
                    onClick={() => toggleSort("member_name")}
                  >
                    <span className="inline-flex items-center">
                      Name
                      <SortIcon field="member_name" />
                    </span>
                  </th>
                  <th
                    className="group text-left text-xs font-medium text-muted-foreground uppercase tracking-wider px-4 py-3 cursor-pointer select-none hover:text-foreground transition-colors"
                    onClick={() => toggleSort("is_visitor")}
                  >
                    <span className="inline-flex items-center">
                      Type
                      <SortIcon field="is_visitor" />
                    </span>
                  </th>
                  <th className="text-left text-xs font-medium text-muted-foreground uppercase tracking-wider px-4 py-3">Email</th>
                  <th className="text-left text-xs font-medium text-muted-foreground uppercase tracking-wider px-4 py-3">Phone</th>
                  <th
                    className="group text-left text-xs font-medium text-muted-foreground uppercase tracking-wider px-4 py-3 cursor-pointer select-none hover:text-foreground transition-colors"
                    onClick={() => toggleSort("status")}
                  >
                    <span className="inline-flex items-center">
                      Status
                      <SortIcon field="status" />
                    </span>
                  </th>
                  <th
                    className="group text-left text-xs font-medium text-muted-foreground uppercase tracking-wider px-4 py-3 cursor-pointer select-none hover:text-foreground transition-colors"
                    onClick={() => toggleSort("checkin_time")}
                  >
                    <span className="inline-flex items-center">
                      Checked In
                      <SortIcon field="checkin_time" />
                    </span>
                  </th>
                  <th className="text-right text-xs font-medium text-muted-foreground uppercase tracking-wider px-4 py-3">Actions</th>
                </tr>
              </thead>
              <tbody>
                {sortedAttendees.length === 0 ? (
                  <tr>
                    <td colSpan={7} className="px-4 py-8 text-center text-sm text-muted-foreground">
                      No attendees match your current search/filter.
                    </td>
                  </tr>
                ) : (
                  sortedAttendees.map((attendee) => {
                    const member = attendee.member_id ? attendeeMemberById.get(attendee.member_id) : null;
                    const displayPhone = attendee.is_visitor ? attendee.phone : member?.phone;
                    const displayEmail = attendee.is_visitor ? attendee.email : member?.email;

                    return (
                      <tr key={attendee.id} className="border-b border-border/50 hover:bg-primary/10 transition-colors">
                        <td className="px-4 py-3">
                          {attendee.member_id && attendeeMemberById.has(attendee.member_id) ? (
                            <button
                              type="button"
                              onClick={() => setViewMember(attendeeMemberById.get(attendee.member_id!) || null)}
                              className="text-left hover:underline underline-offset-2"
                            >
                              <MemberNameCell name={attendee.member_name} size="sm" iconStyle="avatar" />
                            </button>
                          ) : (
                            <MemberNameCell name={attendee.member_name} size="sm" iconStyle="avatar" />
                          )}
                        </td>
                        <td className="px-4 py-3 text-sm">
                          <Badge variant="secondary">{attendee.is_visitor ? "Visitor" : "Member"}</Badge>
                        </td>
                        <td className="px-4 py-3 text-sm text-muted-foreground">{displayEmail || "-"}</td>
                        <td className="px-4 py-3 text-sm text-muted-foreground">{displayPhone || "-"}</td>
                        <td className="px-4 py-3 text-sm text-muted-foreground capitalize">{attendee.status || "present"}</td>
                        <td className="px-4 py-3 text-sm text-muted-foreground">{format(new Date(attendee.checkin_time), "MMM d, yyyy h:mm a")}</td>
                        <td className="px-4 py-3 text-right">
                          <div className="inline-flex items-center gap-1">
                            <Button variant="ghost" size="sm" onClick={() => openEditDialog(attendee)}>
                              <Pencil className="w-4 h-4" />
                            </Button>
                            <Button variant="ghost" size="sm" className="text-destructive hover:text-destructive" onClick={() => setDeleteAttendee(attendee)}>
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
          </div>
        )}
      </div>

      <Dialog open={formOpen} onOpenChange={(open) => {
        setFormOpen(open);
        if (!open) setEditingAttendee(null);
      }}>
        <DialogContent className={editingAttendee ? "sm:max-w-md" : "sm:max-w-lg"}>
          {editingAttendee ? (
            <>
              <DialogHeader>
                <DialogTitle>Edit Attendee</DialogTitle>
              </DialogHeader>
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label>Full name</Label>
                  <Input
                    value={formValues.member_name}
                    onChange={(e) => setFormValues((prev) => ({ ...prev, member_name: e.target.value, member_id: null }))}
                    placeholder="Attendee full name"
                  />
                </div>

                <div className="space-y-2">
                  <Label>Phone</Label>
                  <Input
                    value={formValues.phone}
                    onChange={(e) => setFormValues((prev) => ({ ...prev, phone: e.target.value }))}
                    placeholder="Optional phone"
                  />
                </div>

                {formValues.is_visitor && (
                  <div className="space-y-2">
                    <Label>Email</Label>
                    <Input
                      type="email"
                      value={formValues.email}
                      onChange={(e) => setFormValues((prev) => ({ ...prev, email: e.target.value }))}
                      placeholder="Optional email"
                    />
                  </div>
                )}

                <div className="flex items-center justify-between rounded-lg border border-border p-3">
                  <Label htmlFor="visitor-toggle">Visitor</Label>
                  <Switch
                    id="visitor-toggle"
                    checked={formValues.is_visitor}
                    onCheckedChange={(checked) => setFormValues((prev) => ({
                      ...prev,
                      is_visitor: checked,
                      member_id: checked ? null : prev.member_id,
                    }))}
                  />
                </div>

                <div className="space-y-2">
                  <Label>Status</Label>
                  <Select value={formValues.status} onValueChange={(value) => setFormValues((prev) => ({ ...prev, status: value }))}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="present">Present</SelectItem>
                      <SelectItem value="late">Late</SelectItem>
                      <SelectItem value="absent">Absent</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="flex justify-end gap-2">
                  <Button variant="outline" onClick={() => setFormOpen(false)}>Cancel</Button>
                  <Button
                    onClick={saveAttendee}
                    disabled={updateAttendeeMutation.isPending}
                  >
                    Save changes
                  </Button>
                </div>
              </div>
            </>
          ) : (
            <div className="space-y-6">
              <div className="text-center">
                <div className="w-14 h-14 rounded-xl bg-primary/10 flex items-center justify-center mx-auto mb-4">
                  <Church className="w-7 h-7 text-primary" />
                </div>
                <h2 className="text-2xl font-heading font-bold text-foreground">{event.name}</h2>
                <div className="flex items-center justify-center gap-2 mt-2">
                  <Badge variant="secondary">{event.event_type}</Badge>
                  <span className="text-sm text-muted-foreground">{event.event_date}</span>
                </div>
              </div>

              <div className="flex rounded-lg border border-border overflow-hidden">
                <button
                  type="button"
                  className={`flex-1 py-2.5 text-sm font-medium transition-colors ${!formValues.is_visitor ? "bg-primary text-primary-foreground" : "bg-background text-muted-foreground hover:bg-secondary/50"}`}
                  onClick={() => setFormValues({ ...EMPTY_FORM, is_visitor: false })}
                >
                  Member
                </button>
                <button
                  type="button"
                  className={`flex-1 py-2.5 text-sm font-medium transition-colors ${formValues.is_visitor ? "bg-primary text-primary-foreground" : "bg-background text-muted-foreground hover:bg-secondary/50"}`}
                  onClick={() => setFormValues({ ...EMPTY_FORM, is_visitor: true })}
                >
                  <UserPlus className="w-4 h-4 inline mr-1" /> Visitor
                </button>
              </div>

              <form
                className="space-y-4"
                onSubmit={(e) => {
                  e.preventDefault();
                  saveAttendee();
                }}
              >
                <div className="space-y-2 relative">
                  <Label>{formValues.is_visitor ? "Your Name" : "Search Your Name"}</Label>
                  <Input
                    value={formValues.member_name}
                    onChange={(e) => setFormValues((prev) => ({ ...prev, member_name: e.target.value, member_id: null }))}
                    placeholder={formValues.is_visitor ? "Enter your name" : "Start typing your name..."}
                    autoComplete="off"
                    autoFocus
                  />

                  {!formValues.is_visitor && memberSuggestions.length > 0 && !formValues.member_id && (
                    <div className="absolute z-50 top-full left-0 right-0 mt-1 bg-popover border border-border rounded-lg shadow-lg max-h-48 overflow-y-auto">
                      {memberSuggestions.map((member) => (
                        <button
                          key={member.id}
                          type="button"
                          className="w-full text-left px-4 py-3 text-sm hover:bg-accent/10 transition-colors border-b border-border/50 last:border-0"
                          onClick={() => applyMemberSuggestion(member)}
                        >
                          <p className="font-medium text-foreground">{member.full_name}</p>
                        </button>
                      ))}
                    </div>
                  )}

                  {!formValues.is_visitor && formValues.member_id && (
                    <p className="text-xs text-success font-medium inline-flex items-center gap-1">
                      <CheckCircle2 className="w-3 h-3" /> Member selected
                    </p>
                  )}
                </div>

                {formValues.is_visitor && (
                  <>
                    <div className="space-y-2">
                      <Label>Phone (optional)</Label>
                      <Input
                        value={formValues.phone}
                        onChange={(e) => setFormValues((prev) => ({ ...prev, phone: e.target.value }))}
                        placeholder="Your phone number"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Email (optional)</Label>
                      <Input
                        type="email"
                        value={formValues.email}
                        onChange={(e) => setFormValues((prev) => ({ ...prev, email: e.target.value }))}
                        placeholder="you@example.com"
                      />
                    </div>
                  </>
                )}

                <Button type="submit" className="w-full h-12 text-base" disabled={addAttendeeMutation.isPending || !formValues.member_name.trim()}>
                  {addAttendeeMutation.isPending ? (
                    <><Loader2 className="w-5 h-5 mr-2 animate-spin" /> Checking in...</>
                  ) : (
                    <><CheckCircle2 className="w-5 h-5 mr-2" /> Check In</>
                  )}
                </Button>
              </form>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!deleteAttendee} onOpenChange={(open) => !open && setDeleteAttendee(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete attendee?</AlertDialogTitle>
            <AlertDialogDescription>
              This will remove {deleteAttendee?.member_name || "this attendee"} from the attendance list.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              onClick={() => deleteAttendee && deleteAttendeeMutation.mutate(deleteAttendee.id)}
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <MemberDetailDialog
        open={!!viewMember}
        onClose={() => setViewMember(null)}
        member={viewMember}
        onEdit={() => {
          toast.info("Open People > Members to edit this member profile.");
        }}
      />
    </div>
  );
}
